import React, { FC, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import ImportGridModal from '../ow_flatfile/ImportGridModal';
import {
	getBulkTransferToLocationFlatfileHeader,
	getBulkTransferToSLFlatfileHeader,
} from './asset_bulk_transfer_utils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { Button, Dropdown, Menu, Spin, message } from 'antd';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import moment from 'moment';
import {
	bulkTransferAssetsToLocationsForSupplier,
	getAllAssetsForBulkUpdateForSupplier,
	getAssetsBulkTransferStatusForSupplier,
} from '../../thunks/assets_thunks';
import {
	bulkTransferAssetsToStockLocationsForSupplier,
	getAssetsBulkTransferToStockLocationStatusForSupplier,
} from '../../thunks/equipment_per_stock_locations_thunks';
import { BE_DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import {
	getEquipmentTypeFlatfileOptions,
	getEquipmentTypeValueField,
} from '../stock_locations_detail_details_page/stock_location_constants';
import OWPoll from '../common/OWPoll';
import { REGEX_CONFIG_NAMES, regexRecordHook } from '../ow_flatfile/import_record_hooks';

const BulkTransferAssetsButton: FC<any> = ({
	fetchAllLocations,
	fetchAllStockLocations,
	getTransferStatus,
	bulkTransferAssets,
	onSuccess,
	getTransferToSLStatus,
	bulkTransferAssetsToStockLocation,
	fetchAllEquipmentTypes,
	fetchAllAssets,
	companyConfig,
}): React.ReactElement => {
	const [transferToStockLocationPopupVisible, setTransferToStockLocationPopupVisible] =
		useState(false);
	const [transferToLocationPopupVisible, setTransferToLocationPopupVisible] = useState(false);

	const [initiatingTransfer, setInitiatingTransfer] = useState(false);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	const [allLocations, setAllLocations] = useState([]);
	const [allStockLocations, setAllStockLocations] = useState([]);
	const [allEquipmentTypes, setAllEquipmentTypes] = useState([]);
	const [allAssets, setAllAssets] = useState([]);

	const [transferring, setTransferring] = useState(false);
	const [transferInitiated, setTransferInitiated] = useState(false);

	const canTransfer = useCallback(
		(toStockLocation) => {
			const methodToCall = toStockLocation ? getTransferToSLStatus : getTransferStatus;
			return new Promise((resolve, reject) => {
				methodToCall()
					.then((res) => {
						if (res && res.length > 0) {
							message.error('Bulk transfer already in progress!');
							reject(false);
						} else {
							resolve(true);
						}
					})
					.catch(() => resolve(true));
			});
		},
		[getTransferStatus, getTransferToSLStatus]
	);

	const _showTransferToLocationPopup = useCallback(() => {
		setInitiatingTransfer(true);
		canTransfer(false)
			.then((res) => {
				if (res) {
					fetchAllLocations()
						.then((res) => {
							setAllLocations(res);
							setTransferToLocationPopupVisible(true);
						})
						.catch((err) => message.error(err))
						.finally(() => setInitiatingTransfer(false));
				} else {
					setInitiatingTransfer(false);
				}
			})
			.catch(() => setInitiatingTransfer(false));
	}, [canTransfer, fetchAllLocations]);

	const _hideTransferToLocationPopup = () => setTransferToLocationPopupVisible(false);

	const _showTransferToSLPopup = useCallback(() => {
		setInitiatingTransfer(true);
		canTransfer(true)
			.then((res) => {
				if (res) {
					const promises = [fetchAllStockLocations(), fetchAllEquipmentTypes(), fetchAllAssets()];
					Promise.allSettled(promises)
						.then((res) => {
							const [
								{ value: stockLocations = [] },
								{ value: equipmentTypes = [] },
								{ value: assetTypes = [] },
							] = res;

							setAllStockLocations(stockLocations);
							setAllEquipmentTypes(equipmentTypes);
							setAllAssets(assetTypes);
							setTransferToStockLocationPopupVisible(true);
						})
						.catch((err) => message.error(err))
						.finally(() => setInitiatingTransfer(false));
				} else {
					setInitiatingTransfer(false);
				}
			})
			.catch(() => setInitiatingTransfer(false));
	}, [canTransfer, fetchAllAssets, fetchAllEquipmentTypes, fetchAllStockLocations]);

	const _hideTransferToSLPopup = () => setTransferToStockLocationPopupVisible(false);

	const bulkTransferMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item onClick={_showTransferToLocationPopup}>To Location</Menu.Item>
				<Menu.Item onClick={_showTransferToSLPopup}>To Stock Location</Menu.Item>
			</Menu>
		),
		[_showTransferToLocationPopup, _showTransferToSLPopup]
	);

	const getModifiedEquipmentTypeId = useCallback(
		(record) => {
			const foundEquipmentType = allEquipmentTypes.find(
				(_) => String(getEquipmentTypeValueField(_)) === String(record.equipmentTypeId)
			);
			const foundEquipmentTypeId = nullSafeGetOrElse(
				'id',
				foundEquipmentType,
				record.equipmentTypeId
			);
			return parseInt(foundEquipmentTypeId);
		},
		[allEquipmentTypes]
	);

	const onTransferToLocation = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				setTransferInitiated(true);
				setTransferring(true);
				bulkTransferAssets(
					results.validData.map((obj) => ({
						...obj,
						equipmentTypeId: getModifiedEquipmentTypeId(obj),
						transferDate: obj.transferDate ? obj.transferDate : undefined,
					}))
				)
					.then(() => {
						const msg = 'Transfer initiated successfully!';
						resolve(msg);
						setTimeout(() => {
							_hideTransferToLocationPopup();
						}, 0);
					})
					.catch((err) => {
						reject(err || 'Unable to transfer! Please contact support!');
						setTransferInitiated(false);
					})
					.finally(() => setTransferring(false));
			});
		},
		[bulkTransferAssets, getModifiedEquipmentTypeId]
	);

	const onTransferToStockLocation = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				setTransferInitiated(true);
				setTransferring(true);
				bulkTransferAssetsToStockLocation(
					results.validData.map((obj) => ({
						...obj,
						equipmentTypeId: getModifiedEquipmentTypeId(obj),
						transferDate: obj.transferDate ? obj.transferDate : undefined,
					}))
				)
					.then(() => {
						const msg = 'Transfer initiated successfully!';
						resolve(msg);
						setTimeout(() => {
							_hideTransferToSLPopup();
						}, 0);
					})
					.catch((err) => {
						reject(err || 'Unable to transfer! Please contact support!');
						setTransferInitiated(false);
					})
					.finally(() => setTransferring(false));
			});
		},
		[bulkTransferAssetsToStockLocation, getModifiedEquipmentTypeId]
	);

	const allLocationNames = useMemo(
		() =>
			allLocations.map((_) => ({
				label: _.name,
				value: _.id,
			})),
		[allLocations]
	);

	const allStockLocationNames = useMemo(
		() =>
			allStockLocations.map((_) => ({
				label: _.name,
				value: _.id,
			})),
		[allStockLocations]
	);
	const allEquipmentTypesNames = useMemo(
		() => getEquipmentTypeFlatfileOptions(allEquipmentTypes),
		[allEquipmentTypes]
	);

	const getEquipmentTypeOptionsByAssetId = (assetId) => {
		const asset = allAssets.find((asset) => asset.id == assetId);
		if (asset) {
			return allEquipmentTypesNames.filter((option) => option.assetTypeId == asset.assetTypeId);
		}
		return [];
	};
	const flatfileConfig = useMemo(
		() =>
			getBulkTransferToSLFlatfileHeader(
				allStockLocationNames,
				allEquipmentTypesNames,
				transferNoteRequired
			),
		[allEquipmentTypesNames, allStockLocationNames, transferNoteRequired]
	);

	const onRecordChange = (record) => {
		const assetId = record.get['assetId'];
		const equipmentTypeId = record.get('equipmentTypeId');
		if (!equipmentTypeId) {
			const newOptions = getEquipmentTypeOptionsByAssetId(assetId);
			newOptions[0] && record.set('equipmentTypeId', newOptions[0]);
		}

		return regexRecordHook([REGEX_CONFIG_NAMES.TRANSFER_DATE])(record);
	};

	const onDownloadSLSample = useCallback(() => {
		const csvTitle = `bulk_transfer_to_stock_location_sample`;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: flatfileConfig.map((_) => _.label),
		});

		const sl1 = nullSafeGetOrElse('0.name', allStockLocations, 'Stock location - 1');
		const sl2 = nullSafeGetOrElse('1.name', allStockLocations, 'Stock location - 2');

		const data = [
			{
				assetId: '1234',
				stockLocationId: sl1,
				equipmentTypeId: nullSafeGetOrElse('0.modelName', allEquipmentTypes, 'Equipment Type - 1'),
				transferNote: '',
				transferDate: moment().subtract(1, 'months').endOf('month').format(BE_DATE_ONLY_FORMAT),
			},
			{
				assetId: '5678',
				stockLocationId: sl2,
				equipmentTypeId: nullSafeGetOrElse('0.modelName', allEquipmentTypes, 'Equipment Type - 2'),
				transferNote: 'This is requested by Mr.XXX!',
				transferDate: '',
			},
		];

		csvExporter.generateCsv(data);
	}, [flatfileConfig, allStockLocations, allEquipmentTypes]);

	const onDownloadLocationSample = useCallback(() => {
		const csvTitle = `bulk_transfer_to_location_sample`;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: getBulkTransferToLocationFlatfileHeader(allLocationNames, transferNoteRequired).map(
				(_) => _.label
			),
		});

		const loc1 = nullSafeGetOrElse('0.name', allLocations, 'location - 1');
		const loc2 = nullSafeGetOrElse('1.name', allLocations, 'location - 2');

		const data = [
			{
				assetId: '1234',
				locationId: loc1,
				transferNote: '',
				transferDate: moment().subtract(1, 'months').endOf('month').format(BE_DATE_ONLY_FORMAT),
			},
			{
				assetId: '5678',
				locationId: loc2,
				transferNote: 'This is requested by Mr.XXX!',
				transferDate: '',
			},
		];

		csvExporter.generateCsv(data);
	}, [allLocationNames, allLocations, transferNoteRequired]);

	return (
		<>
			{transferring ? null : (
				<OWPoll
					key="assets-to-sl"
					id="assets-to-sl"
					fetchStatusPromises={[getTransferToSLStatus, getTransferStatus]}
					successMessage="Transferring assets completed!"
					message="Transferring assets in progress!"
					onRefresh={onSuccess}
					successMessageMandatory={transferInitiated}
					renderElementWithProps={(props) => (
						<Dropdown overlay={bulkTransferMenu} {...props}>
							<Button key={1} size="large">
								<div className="flex flex-row items-center">
									<span>Bulk Transfer</span>
									<div className="ml-2">
										<DownOutlined />
									</div>
									{initiatingTransfer ? (
										<div className="ml-2">
											<Spin
												indicator={<LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />}
											/>
										</div>
									) : null}
								</div>
							</Button>
						</Dropdown>
					)}
				/>
			)}
			{transferToStockLocationPopupVisible && (
				<ImportGridModal
					visible
					title="Transfer assets to stock location"
					sampleModalText="Do you want to download sample file?"
					sampleButtonText="Download Sample file"
					onCancel={_hideTransferToSLPopup}
					onDownloadSample={onDownloadSLSample}
					fields={flatfileConfig}
					onDataLoad={onTransferToStockLocation}
					recordHook={onRecordChange}
					onComplete={_hideTransferToSLPopup}
				/>
			)}
			{transferToLocationPopupVisible && (
				<ImportGridModal
					visible
					title="Transfer assets to location"
					sampleModalText="Do you want to download sample file?"
					sampleButtonText="Download Sample file"
					onCancel={_hideTransferToLocationPopup}
					onDownloadSample={onDownloadLocationSample}
					recordHook={regexRecordHook([REGEX_CONFIG_NAMES.TRANSFER_DATE])}
					fields={getBulkTransferToLocationFlatfileHeader(allLocationNames, transferNoteRequired)}
					onDataLoad={onTransferToLocation}
					onComplete={_hideTransferToLocationPopup}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchAllLocations: () =>
		dispatch(locationsRestCrudThunksForSupplier.readLite({ no_pagination: true })),
	fetchAllAssets: (params) => dispatch(getAllAssetsForBulkUpdateForSupplier(params)),
	fetchAllStockLocations: () =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readLite({ no_pagination: true })),
	fetchAllEquipmentTypes: () =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readLite({ no_pagination: true })),
	getTransferStatus: () => dispatch(getAssetsBulkTransferStatusForSupplier()),
	bulkTransferAssets: (entities) => dispatch(bulkTransferAssetsToLocationsForSupplier(entities)),
	getTransferToSLStatus: () => dispatch(getAssetsBulkTransferToStockLocationStatusForSupplier()),
	bulkTransferAssetsToStockLocation: (entities) =>
		dispatch(bulkTransferAssetsToStockLocationsForSupplier(entities)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkTransferAssetsButton);
